import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { cn } from '../../../lib/utils';

// `group/radio` é o grupo NOMEADO que leva o aria-invalid aos itens (campo com
// erro, Figma 20080:1334; decisão de produto da rodada 2 do QA, D27.1 — mantida
// na rodada 3) — cada item já é `group` por conta própria (o indicador depende
// disso), então o grupo precisa de nome para não colidir.
const radioCardGroupVariants = cva('group/radio flex flex-wrap gap-2');

export interface RadioCardGroupProps
  extends
    ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
    VariantProps<typeof radioCardGroupVariants> {}

const RadioCardGroup = forwardRef<ElementRef<typeof RadioGroupPrimitive.Root>, RadioCardGroupProps>(
  ({ className, ...props }, ref) => (
    <RadioGroupPrimitive.Root
      ref={ref}
      data-slot="radio-card-group"
      className={cn(radioCardGroupVariants(), className)}
      {...props}
    />
  )
);
RadioCardGroup.displayName = 'RadioCardGroup';

const radioCardItemVariants = cva(
  [
    // `group` is load-bearing: the RadioDot fills via `group-data-[state=checked]`,
    // which needs this element (the Radix Item that carries `data-state`) to be
    // the group root. Baked in here so consumers never have to remember it.
    'group flex cursor-pointer select-none items-start gap-2 rounded-lg border border-border bg-background px-4 py-3',
    'text-sm font-medium text-foreground transition-colors',
    'hover:border-primary',
    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
    'data-[state=checked]:border-primary data-[state=checked]:bg-primary/10',
    // Grupo inválido (aria-invalid no RadioCardGroup, vindo do FormFieldControl):
    // borda destructive + anel de 3px destructive-60 em cada item.
    'group-aria-[invalid=true]/radio:border-destructive group-aria-[invalid=true]/radio:ring-[3px] group-aria-[invalid=true]/radio:ring-destructive-60',
  ].join(' '),
  {
    variants: {
      variant: {
        default: '',
        destructive:
          'border-destructive ring-[3px] ring-destructive-60 data-[state=checked]:border-destructive',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface RadioCardItemProps
  extends
    ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    VariantProps<typeof radioCardItemVariants> {
  /** Hide the radio dot indicator */
  hideDot?: boolean;
}

const RadioCardItem = forwardRef<ElementRef<typeof RadioGroupPrimitive.Item>, RadioCardItemProps>(
  ({ className, variant, hideDot = false, children, ...props }, ref) => (
    <RadioGroupPrimitive.Item
      ref={ref}
      data-slot="radio-card-item"
      className={cn(radioCardItemVariants({ variant }), className)}
      {...props}
    >
      {!hideDot && <RadioDot />}
      {children}
    </RadioGroupPrimitive.Item>
  )
);
RadioCardItem.displayName = 'RadioCardItem';

function RadioDot() {
  return (
    <span
      className={cn(
        // `mt-0.5` alinha o indicador (size-4) ao centro da 1ª linha do texto
        // (leading-5) agora que o item é `items-start` (topo), não centralizado.
        'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-border bg-background shadow-2xs',
        'transition-[border-width]',
        'group-data-[state=checked]:border-[5px] group-data-[state=checked]:border-primary'
      )}
      aria-hidden
    />
  );
}

export { RadioCardGroup, radioCardGroupVariants, RadioCardItem, radioCardItemVariants };
